import Style from 'ol/style/Style';

import { PhotoLayerFeatureProperty as FeatureProperty } from './photo-layer-feature-property';
import { DEFAULT_IMAGE_SIZE, EXPLODED_IMAGE_SIZE } from './photo-layer-feature-layout';

export const defaultStyle = {
  backgroundColor: 'blue',
  borderWidth: 2,
  color: 'white',
  fontFamily: 'Arial',
  fontSize: 12,
  imageSize: DEFAULT_IMAGE_SIZE,
  pillRadius: 11,
  shadowBlur: 5,
  shadowOffset: 5,
  shadowColor: '#999'
};

const explodedStyle = Object.assign({}, defaultStyle, {
  imageSize: EXPLODED_IMAGE_SIZE,
  lineColor: 'gray',
  lineRadius: 10,
  lineWidth: 1
});

export function photoLayerStyleFunction (feature) {
  return new Style({
    renderer: feature.get(FeatureProperty.LAYOUT) ? explodedStyleRenderer : defaultStyleRenderer
  });
}

function explodedStyleRenderer ([ x, y ], { context, feature, pixelRatio }) {
  let attachmentPositions = feature.get(FeatureProperty.LAYOUT).getAttachmentPositions();
  const attachments = feature.get(FeatureProperty.ATTACHMENTS);
  const animationProgress = feature.get(FeatureProperty.ANIMATION_PROGRESS);
  const style = explodedStyle;

  context.save();
  context.translate(x, y);
  context.scale(pixelRatio, pixelRatio);

  let imageSize = style.imageSize;
  if (animationProgress >= 0 && animationProgress < 1) {
    imageSize = defaultStyle.imageSize + animationProgress * (explodedStyle.imageSize - defaultStyle.imageSize);
    attachmentPositions = attachmentPositions.map(([ layoutX, layoutY ]) => [ layoutX * animationProgress, layoutY * animationProgress ]);
  }

  attachmentPositions.forEach(([ layoutX, layoutY ]) => {
    drawLine(context, layoutX, layoutY, style);
  });

  attachmentPositions.forEach(([ layoutX, layoutY ], i) => {
    context.save();
    context.translate(layoutX, layoutY);
    drawImageWithBorder(context, attachments[i].thumbnail, attachments[i].shadowRendered, imageSize, style);
    context.restore();
  });

  const additionalImagesCount = Math.max(0, attachments.length - attachmentPositions.length);
  if (additionalImagesCount > 0) {
    const imageSize = style.imageSize;
    context.translate(attachmentPositions[0][0], attachmentPositions[0][1]);
    context.translate(imageSize / 2, -imageSize / 2);
    drawTextPill(context, additionalImagesCount, style);
  }
  context.restore();
}

function defaultStyleRenderer ([ x, y ], { context, feature, pixelRatio }) {
  const style = defaultStyle;
  const attachments = feature.get(FeatureProperty.ATTACHMENTS);
  const imageSize = style.imageSize;

  context.save();
  context.translate(x, y);
  context.scale(pixelRatio, pixelRatio);
  drawImageWithBorder(context, attachments[0].thumbnail, attachments[0].shadowRendered, imageSize, style);
  context.translate(imageSize / 2, -imageSize / 2);
  drawTextPill(context, attachments.length, style);
  context.restore();
}

function drawImageWithBorder (context, img, shadowRendered, imageSize, {
  color: borderColor,
  borderWidth,
  shadowBlur,
  shadowOffset,
  shadowColor
}) {
  context.save();
  context.translate(-imageSize / 2, -imageSize / 2);

  if (shadowRendered) {
    context.drawImage(img, -imageSize, -imageSize, imageSize * 3, imageSize * 3);
  } else {
    drawSimpleShadow(context, shadowColor, shadowBlur, shadowOffset, imageSize);
    context.drawImage(img, 0, 0, imageSize, imageSize);
  }

  context.lineWidth = borderWidth;
  context.strokeStyle = borderColor;
  context.strokeRect(0, 0, imageSize, imageSize);
  context.restore();
}

function drawSimpleShadow (context, shadowColor, shadowBlur, shadowOffset, imageSize) {
  context.save();
  context.fillStyle = shadowColor;
  context.shadowBlur = shadowBlur;
  context.shadowColor = shadowColor;
  context.shadowOffsetX = shadowOffset;
  context.shadowOffsetY = shadowOffset;
  context.fillRect(0, 0, imageSize, imageSize);
  context.restore();
}

function drawTextPill (context, text, {
  color,
  backgroundColor,
  borderColor = color,
  borderWidth,
  fontFamily,
  fontSize,
  pillRadius
}) {
  context.save();
  context.font = `${fontSize}px ${fontFamily}`;

  const textWidth = context.measureText(text).width;
  const pillWidth = Math.max(0, textWidth - pillRadius);

  context.beginPath();
  context.arc(-pillWidth / 2, 0, pillRadius, 0.5 * Math.PI, 1.5 * Math.PI);
  context.lineTo(pillWidth / 2, -pillRadius);
  context.arc(pillWidth / 2, 0, pillRadius, 1.5 * Math.PI, 0.5 * Math.PI);
  context.closePath();

  context.fillStyle = backgroundColor;
  context.fill();
  context.lineWidth = borderWidth;
  context.strokeStyle = borderColor;
  context.stroke();

  context.fillStyle = color;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, 0, 0);
  context.restore();
}

function drawLine (context, toX, toY, {
  lineColor,
  lineRadius,
  lineWidth
}) {
  let fromX = 0;
  let fromY = 0;

  const distance = Math.sqrt(toX * toX + toY * toY);

  if (lineRadius > 0 && distance > lineRadius) {
    const scaleFactor = lineRadius / distance;
    fromX = scaleFactor * toX;
    fromY = scaleFactor * toY;
  }

  context.save();
  context.beginPath();
  context.moveTo(fromX, fromY);
  context.lineTo(toX, toY);
  context.lineWidth = lineWidth;
  context.strokeStyle = lineColor;
  context.stroke();
  context.restore();
}
